import type {
  Settings as LayoutSettings,
  MenuDataItem,
} from '@ant-design/pro-components';
import type { MouseEvent } from 'react';
import { AvatarDropdown, AvatarName } from '@/components';
import { filterAdminRoleIds } from '@/constants/adminRoles';
import { permissionService, useMenuPermission } from '@/permissions';
import {
  fetchUserRoles,
  getAuthToken,
  getCurrentUser,
  persistCurrentUser,
} from '@/services/user/api';
import { resolveMediaUrl } from '@/utils/mediaUrl';
import '@ant-design/v5-patch-for-react-19';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { App, ConfigProvider, message } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import * as React from 'react';
import defaultSettings from '../config/defaultSettings';

const loginPath = '/user/login';
const ADMIN_BUSINESS_FALLBACK_PATH = '/geofence';
const SYSTEM_ADMIN_FALLBACK_PATH = '/system/organization-structure';

const stopMenuHeaderNavigation = (event: MouseEvent<HTMLDivElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  historyTabs?: API.HistoryTabItem[];
  setHistoryTabs?: (tabs: API.HistoryTabItem[]) => void;
}> {
  const fetchUserInfo = async () => {
    try {
      const userData = await getCurrentUser({
        skipErrorHandler: true,
      });
      const isSystemAdmin = Boolean(userData.is_system_admin);
      let roleIds = isSystemAdmin
        ? []
        : filterAdminRoleIds(
            Array.isArray(userData.role_ids) ? userData.role_ids : [],
          );

      if (!isSystemAdmin && roleIds.length === 0) {
        try {
          const roleResponse = await fetchUserRoles(
            {
              page: 1,
              page_size: 100,
            },
            {
              skipErrorHandler: true,
            },
          );
          roleIds = filterAdminRoleIds(
            (roleResponse.items || []).map((item) => item.role_id),
          );
          if (roleIds.length > 0) {
            persistCurrentUser({
              ...userData,
              role_ids: roleIds,
            });
          }
        } catch (_roleError) {
          roleIds = [];
        }
      }

      // 将你们的用户数据格式转换为API.CurrentUser格式
      return {
        name: userData.name,
        username: userData.username,
        userid: userData.id?.toString(),
        email: '',
        signature: '',
        title: '',
        group: '',
        tags: [],
        notifyCount: 0,
        unreadCount: 0,
        country: 'China',
        access: 'admin',
        geographic: {
          province: { label: '', key: '' },
          city: { label: '', key: '' },
        },
        address: '',
        phone: userData.phone,
        avatar: resolveMediaUrl(userData.avatar_url),
        roleIds,
        isSystemAdmin,
      };
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
      historyTabs: [],
      setHistoryTabs: (_tabs: API.HistoryTabItem[]) => {
        // 这里会在 layout 配置中实现
      },
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
    historyTabs: [],
    setHistoryTabs: (_tabs: API.HistoryTabItem[]) => {
      // 这里会在 layout 配置中实现
    },
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 实现 setHistoryTabs 函数
  const setHistoryTabs = (tabs: API.HistoryTabItem[]) => {
    setInitialState((s) => ({
      ...s,
      historyTabs: tabs,
    }));
  };

  // 更新全局状态中的 setHistoryTabs 函数
  if (initialState) {
    initialState.setHistoryTabs = setHistoryTabs;
  }

  // 使用菜单权限控制Hook
  const { filterMenuData } = useMenuPermission();
  const currentRoleIds = initialState?.currentUser?.roleIds || [];
  permissionService.setRoleIds(currentRoleIds);
  permissionService.setSystemAdmin(
    Boolean(initialState?.currentUser?.isSystemAdmin),
  );
  permissionService.setCurrentUsername(initialState?.currentUser?.username);

  return {
    // actionsRender: () => [
    //   <Question key="doc" />,
    //   <SelectLang key="SelectLang" />,
    // ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    pageTitleRender: false,
    // footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
        return;
      }
      if (
        initialState?.currentUser &&
        location.pathname === '/dashboard' &&
        !permissionService.hasRoutePermission('/dashboard') &&
        permissionService.hasRoutePermission(SYSTEM_ADMIN_FALLBACK_PATH)
      ) {
        history.replace(SYSTEM_ADMIN_FALLBACK_PATH);
        return;
      }
      if (
        initialState?.currentUser &&
        location.pathname === '/' &&
        !permissionService.hasRoutePermission('/dashboard')
      ) {
        const fallbackPath = permissionService.hasRoutePermission(
          SYSTEM_ADMIN_FALLBACK_PATH,
        )
          ? SYSTEM_ADMIN_FALLBACK_PATH
          : ADMIN_BUSINESS_FALLBACK_PATH;
        history.replace(fallbackPath);
        return;
      }
      if (
        initialState?.currentUser &&
        !permissionService.hasRoutePermission(location.pathname)
      ) {
        history.push('/403');
      }
    },
    // bgLayoutImgList: [
    //   {
    //     src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr",
    //     left: 85,
    //     bottom: 100,
    //     height: "303px",
    //   },
    //   {
    //     src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr",
    //     bottom: -68,
    //     right: -45,
    //     height: "303px",
    //   },
    //   {
    //     src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr",
    //     bottom: 0,
    //     left: 0,
    //     width: "331px",
    //   },
    // ],
    // links: isDev
    //   ? [
    //       <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
    //         <LinkOutlined />
    //         <span>OpenAPI 文档</span>
    //       </Link>,
    //     ]
    //   : [],
    collapsedButtonRender: false,
    breadcrumbRender: false,
    onMenuHeaderClick: stopMenuHeaderNavigation,
    menuHeaderRender: (logo, title) => (
      <div
        onClick={stopMenuHeaderNavigation}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'default',
          pointerEvents: 'none',
        }}
      >
        {logo}
        {title}
      </div>
    ),
    // 菜单权限控制
    menuDataRender: (menuData: MenuDataItem[]) => {
      return filterMenuData(menuData);
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      if (initialState?.loading) return <div>加载中...</div>;
      return (
        <ConfigProvider
          locale={zhCN}
          theme={{
            token: {
              borderRadius: 4,
            },
          }}
        >
          <App>
            {/* <HistoryTabs /> */}
            {children}
            {/* {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )} */}
          </App>
        </ConfigProvider>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: '',
  timeout: 600000,
  errorConfig: {
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      if (error.response) {
        const payload = error.response?.data;
        if (payload && typeof payload === 'object') {
          const serverMessage = payload.message || payload.msg;
          if (serverMessage) {
            message.error(serverMessage);
            return;
          }
        }
        message.error(
          `请求错误 ${error.response.status}: ${error.response.statusText}`,
        );
      } else if (error.request) {
        message.error('网络错误，请检查您的网络连接');
      } else {
        // message.error("请求配置错误");
      }
    },
  },
  requestInterceptors: [
    (config: any) => {
      const token = getAuthToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
  ],
  responseInterceptors: [
    (response: any) => {
      const { data, config } = response;

      if (config.responseType === 'blob') {
        return response;
      }

      if (
        data &&
        typeof data === 'object' &&
        'code' in data &&
        Number(data.code) !== 0
      ) {
        const errorMessages: { [key: number]: string } = {
          // 400 - 参数错误
          400000: '请求参数错误',

          // 401 - 认证错误
          401001: '用户未登录',

          // 403 - 权限错误
          403001: '用户未授权',

          // 404 - 资源错误
          400001: '资源未找到',
          404001: '资源未找到',

          // 500 - 服务器错误
          500001: '服务器错误',
        };

        const serverMessage = data.message || data.msg;
        const errorMessage =
          serverMessage || errorMessages[data.code] || '未知错误';
        message.error(errorMessage);

        // 如果是认证错误，跳转到登录页
        if (data.code === 401001) {
          history.push('/user/login');
        }

        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = {
          code: data.code,
          msg: data.msg || data.message,
          data: data.data,
        };
        throw error;
      }

      return response.data;
    },
  ],
};
